import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/ui/cn';

/**
 * Deep-link field focus for {@link SchemaForm}. The global search routes to a content editor
 * with the matched term in the URL (`?q=…`); the editor resolves that to a dot-path and the
 * matching field scrolls into view and pulses once, so the user lands on *exactly* the field
 * that matched — the same affordance page copy already has (see settings/PageCopyFields).
 *
 * Content search hits (unlike page copy) carry no field-level data from the API, so the path is
 * derived client-side from the loaded document; this module is purely the focus/scroll/pulse
 * plumbing, agnostic to how the path was found.
 */
const FocusContext = createContext<{ path: string | null }>({ path: null });

export function DeepLinkFocusProvider({
  path,
  children,
}: {
  path: string | null;
  children: ReactNode;
}) {
  return <FocusContext.Provider value={{ path }}>{children}</FocusContext.Provider>;
}

/** How long the deep-link pulse stays lit before it fades out (matches page-copy). */
const PULSE_MS = 1800;

/**
 * Returns a ref + a transient `highlighted` flag for the field whose dot-`path` matches the
 * active deep-link target. On a match it scrolls into view and lights up once, then fades —
 * a one-shot pulse, not a permanent marker. Off-target fields are inert.
 */
function useDeepLinkFocus(path: string) {
  const { path: target } = useContext(FocusContext);
  const isMatch = target != null && target === path;
  const ref = useRef<HTMLDivElement>(null);
  const [highlighted, setHighlighted] = useState(false);
  useEffect(() => {
    if (!isMatch) {
      setHighlighted(false);
      return;
    }
    setHighlighted(true);
    // Defer the scroll a frame so layout is settled before measuring.
    const raf = requestAnimationFrame(() =>
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
    );
    const timer = setTimeout(() => setHighlighted(false), PULSE_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [isMatch]);
  return { ref, highlighted };
}

/**
 * Wraps a single form field so it can be the deep-link focus target. The wrapper is visually
 * inert until matched (no provider, or a different target → just a plain block), then pulses a
 * focus ring once when the search deep-link points at `path`.
 */
export function FieldFocus({ path, children }: { path: string; children: ReactNode }) {
  const { ref, highlighted } = useDeepLinkFocus(path);
  return (
    <div
      ref={ref}
      className={cn(
        'transition-shadow ease-almanac',
        highlighted
          ? 'rounded-md shadow-focus ring-1 ring-accent-blue duration-almanac'
          : 'duration-[1200ms]',
      )}
    >
      {children}
    </div>
  );
}
